import React from 'react'

import CacheBuster from 'react-cache-buster'

import packageInfo from '../package.json'

import { QueryClient, QueryClientProvider } from 'react-query'

import { ReactQueryDevtools } from 'react-query/devtools'
import { Routes, BrowserRouter, Navigate } from 'react-router-dom'
import { ApplicationRoutes } from './ApplicationRoutes'

import { ThemeProvider } from '@shared/theme/theme.provider.component'

import { AuthProvider } from '@modules/auth/auth.provider.component'

import { UpdateHandler } from '@shared/components/update-handler.component'
import { StoreProvider } from '@shared/hooks/use-store.hook'
import { initialState, reducer } from '@shared/lib/reducer'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import useMaintenanceMode from '@shared/hooks/use-is-maintenance-mode'
import MaintenanceScreen from './MaintenanceScreen'
import { Layout } from '@modules/layout/layout.component'
import { StyledEngineProvider, Theme } from '@mui/material/styles'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

declare module '@mui/material/styles' {
  interface Palette {
    defaultButton: Palette['primary']
  }

  interface PaletteOptions {
    defaultButton?: PaletteOptions['primary']
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    defaultButton: true
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

const hostname = process.env.REACT_APP_HOST || ''
const isDev =
  (hostname.length > 0 &&
    window.location.origin.includes(
      'https://dev-ecoms-audyty.azurewebsites.net'
    )) ||
  process.env.NODE_ENV === 'development'

function App() {
  const { isMaintenanceMode, isTester } = useMaintenanceMode()

  if (isMaintenanceMode && !isTester) {
    return <MaintenanceScreen />
  }

  return (
    <CacheBuster
      currentVersion={packageInfo.version}
      isEnabled={true}
      isVerboseMode={false}
      loadingComponent={<BackdropProgress />}
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider>
            <StoreProvider initialState={initialState} reducer={reducer}>
              <AuthProvider>
                <BrowserRouter>
                  <div className="App">
                    <UpdateHandler>
                      <Layout showIndicator={isDev}>
                        <ApplicationRoutes />
                      </Layout>
                    </UpdateHandler>
                  </div>
                </BrowserRouter>
              </AuthProvider>
            </StoreProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </QueryClientProvider>
    </CacheBuster>
  )
}

export default App

/**
 * @todo replace potential reduce/map/find with _.[method] versions
 * @todo use https://react-query.tanstack.com/docs/guides/initial-query-data#initial-data instead of manual fallback
 * @todo dont destructure props
 * @todo put queries in one place or in separate files in given module scope with separate cache keys factories to invalidate query somewher eelse
 * @todo consider react-query cache warm-up when starting audit so it's available when offline (templates for example)
 * @todo useIsFetching for checking is something is fetched and indicate globally
 * @todo use custom hook to handle errors global https://codesandbox.io/s/github/tannerlinsley/react-query/tree/master/examples/custom-hooks
 * @todo rename components and variables to alignw with entities
 * @todo dont use `isLoading` for rendering query data as it can be not there yet because of error
 * @todo create generic button with loading/disabled status when async operation is done on click
 * @todo remove mobx if not needed now
 * @todo unify material-ui imports and add sorting
 * @todo passing requirement by state will fail on redirect, should fetch it separately if not present?
 * @todo investigate workbox from CRA 4.0
 * @todo consider using react-hooks-form
 * @todo investigate `shared` module to be split to `utils`, `shared-components`
 * @todo re-usable page Header component
 * @todo re-usable form buttons component
 * @todo re-usable 2 columns labe: value component
 * @todo use Material-UI Link for non styled Link
 * @todo unify API error handlin in API utils
 * @todo move use-styles to component files
 * @todo if user is online and 401 received, redirect to logn
 * @todo dont cache login response in service-worker
 * @todo instead of passing data to state when going to requirement, connect Auditrequirements with Requirement query
 * @todo on admin, reset search when changing endpoint
 * @todo consider mobx if app will use a lot of state
 * @todo fix react-query parametrized cache keys (use tuple or second parameter)
 * @todo hoist DTO errors handling from CRUD and login to one component
 * @todo investigate better showing of requirements - instead of details pane, use groupping and hide grouping bar https://material-table.com/#/docs/features/grouping
 * @todo login error alert can be re-used on multiple pages perhaps?
 */